import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ARCA_GREEN = '#6B8E23';

const ContactSubmissions = ({ user }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchMembers();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/communication/contact/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSubmissions(response.data.data.submissions || []);
      }
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      toast.error('Failed to load contact submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const viewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
  };

  const updateStatus = async (submissionId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${API_URL}/communication/contact/submissions/${submissionId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Submission status updated successfully!');
      fetchSubmissions();
      setShowModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status. Please try again.');
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/communication/contact/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Submission deleted successfully!');
      fetchSubmissions();
      setShowModal(false);
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission. Please try again.');
    }
  };

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/members/search`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const membersData = response.data.data || [];
        setMembers(membersData);
        console.log(`✅ Loaded ${membersData.length} members`);
        if (membersData.length === 0) {
          toast.info('No members found in the system');
        }
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load members. Please try again.');
    } finally {
      setLoadingMembers(false);
    }
  };

  const openMessageModal = (submission) => {
    setSelectedSubmission(submission);
    setShowMessageModal(true);
    setMessageContent('');
    setSelectedMemberIds([]);
    setSelectAll(false);

    // Refresh members list when opening modal
    if (members.length === 0) {
      fetchMembers();
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedMemberIds([]);
      setSelectAll(false);
    } else {
      setSelectedMemberIds(members.map(m => m.id));
      setSelectAll(true);
    }
  };

  const toggleMemberSelection = (memberId) => {
    setSelectedMemberIds(prev => {
      if (prev.includes(memberId)) {
        const newSelection = prev.filter(id => id !== memberId);
        setSelectAll(newSelection.length === members.length);
        return newSelection;
      } else {
        const newSelection = [...prev, memberId];
        setSelectAll(newSelection.length === members.length);
        return newSelection;
      }
    });
  };

  const sendDirectMessage = async () => {
    if (selectedMemberIds.length === 0) {
      toast.error('Please select at least one member to message');
      return;
    }

    if (!messageContent.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('adminToken');

      // Use the new admin messaging endpoint
      const response = await axios.post(
        `${API_URL}/communication/admin/messages/send`,
        {
          recipient_ids: selectedMemberIds,
          subject: `Re: ${selectedSubmission.subject}`,
          content: messageContent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { successful, failed } = response.data.data;
        if (successful > 0) {
          toast.success(`Message sent successfully to ${successful} member${successful > 1 ? 's' : ''}!`);
        }
        if (failed > 0) {
          toast.warning(`Failed to send message to ${failed} member${failed > 1 ? 's' : ''}`);
        }

        setShowMessageModal(false);
        setMessageContent('');
        setSelectedMemberIds([]);
        setSelectAll(false);
      }
    } catch (error) {
      console.error('Error sending messages:', error);
      toast.error(error.response?.data?.message || 'Failed to send messages. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const getFilteredSubmissions = () => {
    return submissions.filter(sub => {
      const matchesStatus = filterStatus === 'all' || sub.status === filterStatus;
      const matchesSearch =
        sub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.inquiry_type?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      new: 'bg-primary',
      in_progress: 'bg-warning',
      resolved: 'bg-success',
      archived: 'bg-secondary'
    };
    return statusColors[status] || 'bg-secondary';
  };

  const getInquiryTypeBadge = (type) => {
    const typeColors = {
      general: 'bg-info',
      membership: 'bg-primary',
      event: 'bg-success',
      partnership: 'bg-warning',
      support: 'bg-danger',
      feedback: 'bg-indigo',
      other: 'bg-secondary'
    };
    return typeColors[type] || 'bg-secondary';
  };

  if (loading) {
    return <div className="text-center py-4">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-2 text-muted">Loading contact submissions...</p>
    </div>;
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="text-3xl font-bold text-dark">Contact Submissions</h1>
        <div className="text-sm text-muted">
          Total Submissions: <span className="fw-bold">{submissions.length}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 row g-3">
        <div className="col-md-6">
          <input
            type="text"
            placeholder="Search by name, email, or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
          />
        </div>
        <div className="col-md-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="form-select"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="card shadow rounded-lg overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredSubmissions().length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    No contact submissions found
                  </td>
                </tr>
              ) : (
                getFilteredSubmissions().map((submission) => (
                  <tr key={submission.id}>
                    <td>
                      <div className="fw-medium text-dark">{submission.name}</div>
                    </td>
                    <td className="text-muted">
                      {submission.email}
                    </td>
                    <td>
                      <div className="text-dark">{submission.subject}</div>
                    </td>
                    <td>
                      <span className={`badge ${getInquiryTypeBadge(submission.inquiry_type)}`}>
                        {submission.inquiry_type}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(submission.status)}`}>
                        {submission.status === 'in_progress' ? 'In Progress' : submission.status}
                      </span>
                    </td>
                    <td className="text-muted">
                      {new Date(submission.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        onClick={() => viewSubmission(submission)}
                        className="btn btn-sm btn-outline-primary me-2"
                      >
                        View
                      </button>
                      <button
                        onClick={() => deleteSubmission(submission.id)}
                        className="btn btn-sm btn-outline-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Submission Modal */}
      {showModal && selectedSubmission && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h3 className="modal-title">Contact Submission Details</h3>
                <button onClick={() => setShowModal(false)} className="btn-close" type="button" aria-label="Close"></button>
              </div>

              <div className="modal-body">
                <div className="row g-4">
                  {/* Contact Information */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">Contact Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Name</label>
                          <p className="mb-0">{selectedSubmission.name}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Email</label>
                          <p className="mb-0">
                            <a href={`mailto:${selectedSubmission.email}`} className="text-primary">
                              {selectedSubmission.email}
                            </a>
                          </p>
                        </div>
                        {selectedSubmission.phone && (
                          <div className="col-md-6">
                            <label className="form-label">Phone</label>
                            <p className="mb-0">{selectedSubmission.phone}</p>
                          </div>
                        )}
                        {selectedSubmission.organization && (
                          <div className="col-md-6">
                            <label className="form-label">Organization</label>
                            <p className="mb-0">{selectedSubmission.organization}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Inquiry Details */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">Inquiry Details</h4>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Subject</label>
                          <p className="mb-0">{selectedSubmission.subject}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Inquiry Type</label>
                          <p className="mb-0">
                            <span className={`badge ${getInquiryTypeBadge(selectedSubmission.inquiry_type)}`}>
                              {selectedSubmission.inquiry_type}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="form-label mb-2">Message</label>
                        <div className="bg-white p-3 rounded border">
                          <p className="mb-0">{selectedSubmission.message}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Admin Notes */}
                  {selectedSubmission.admin_notes && (
                    <div className="col-12">
                      <div className="bg-light p-3 rounded">
                        <h4 className="fw-semibold mb-3">Admin Notes</h4>
                        <p className="mb-0">{selectedSubmission.admin_notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="col-12">
                    <div className="bg-light p-3 rounded">
                      <h4 className="fw-semibold mb-3">Metadata</h4>
                      <div className="row g-3 text-sm">
                        <div className="col-md-6">
                          <label className="form-label">Submitted</label>
                          <p className="mb-0 text-dark">
                            {new Date(selectedSubmission.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Last Updated</label>
                          <p className="mb-0 text-dark">
                            {new Date(selectedSubmission.updated_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="w-100">
                  <h4 className="fw-semibold mb-3">Update Status</h4>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      onClick={() => updateStatus(selectedSubmission.id, 'new')}
                      className="btn btn-primary"
                    >
                      Mark as New
                    </button>
                    <button
                      onClick={() => updateStatus(selectedSubmission.id, 'in_progress')}
                      className="btn btn-warning"
                    >
                      Mark as In Progress
                    </button>
                    <button
                      onClick={() => updateStatus(selectedSubmission.id, 'resolved')}
                      className="btn btn-success"
                    >
                      Mark as Resolved
                    </button>
                    <button
                      onClick={() => updateStatus(selectedSubmission.id, 'archived')}
                      className="btn btn-secondary"
                    >
                      Archive
                    </button>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => openMessageModal(selectedSubmission)}
                      className="btn btn-success"
                      style={{ backgroundColor: ARCA_GREEN, borderColor: ARCA_GREEN }}
                    >
                      <i className="bi bi-chat-dots me-2"></i>
                      Send Message to Members
                    </button>
                    <a
                      href={`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject}`}
                      className="btn btn-primary"
                    >
                      <i className="bi bi-envelope me-2"></i>
                      Reply via Email
                    </a>
                    <button
                      onClick={() => setShowModal(false)}
                      className="btn btn-secondary"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && selectedSubmission && (
        <div className="modal-backdrop show"></div>
      )}

      {/* Send Message Modal */}
      {showMessageModal && selectedSubmission && (
        <>
          <div className="modal d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header" style={{ borderBottom: `3px solid ${ARCA_GREEN}` }}>
                  <div>
                    <h4 className="modal-title mb-1">
                      <i className="bi bi-chat-dots text-success me-2"></i>
                      Send Direct Message
                    </h4>
                    <small className="text-muted">Reply to: {selectedSubmission.name}</small>
                  </div>
                  <button
                    onClick={() => setShowMessageModal(false)}
                    className="btn-close"
                    type="button"
                    aria-label="Close"
                  ></button>
                </div>

                <div className="modal-body">
                  {/* Original Inquiry Context */}
                  <div className="alert alert-light border mb-4">
                    <h6 className="fw-semibold mb-2">
                      <i className="bi bi-info-circle text-primary me-2"></i>
                      Original Inquiry
                    </h6>
                    <div className="mb-2">
                      <strong>From:</strong> {selectedSubmission.name} ({selectedSubmission.email})
                    </div>
                    <div className="mb-2">
                      <strong>Subject:</strong> {selectedSubmission.subject}
                    </div>
                    <div>
                      <strong>Message:</strong>
                      <p className="mb-0 mt-1 text-muted small">
                        {selectedSubmission.message?.substring(0, 200)}
                        {selectedSubmission.message?.length > 200 && '...'}
                      </p>
                    </div>
                  </div>

                  {/* Select Members */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-semibold mb-0">
                        <i className="bi bi-people-fill me-2" style={{ color: ARCA_GREEN }}></i>
                        Select Members to Message
                      </label>
                      <div>
                        {!loadingMembers && members.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm me-2"
                            onClick={toggleSelectAll}
                            style={{
                              backgroundColor: selectAll ? ARCA_GREEN : '#f8f9fa',
                              color: selectAll ? 'white' : ARCA_GREEN,
                              borderColor: ARCA_GREEN
                            }}
                          >
                            <i className={`bi ${selectAll ? 'bi-check-square' : 'bi-square'} me-1`}></i>
                            {selectAll ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={fetchMembers}
                          disabled={loadingMembers}
                        >
                          <i className={`bi bi-arrow-clockwise me-1 ${loadingMembers ? 'spinner-border spinner-border-sm' : ''}`}></i>
                          {loadingMembers ? 'Loading...' : 'Refresh'}
                        </button>
                        {selectedMemberIds.length > 0 && (
                          <span className="badge" style={{ backgroundColor: ARCA_GREEN }}>
                            {selectedMemberIds.length} selected
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      className="border rounded p-3"
                      style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        borderColor: ARCA_GREEN + '40'
                      }}
                    >
                      {loadingMembers ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-success" role="status" style={{ color: ARCA_GREEN }}>
                            <span className="visually-hidden">Loading members...</span>
                          </div>
                          <p className="text-muted mt-2 mb-0">Loading members...</p>
                        </div>
                      ) : members.length === 0 ? (
                        <div className="text-center py-4">
                          <i className="bi bi-inbox display-4 text-muted d-block mb-2"></i>
                          <p className="text-muted mb-2">No members found</p>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={fetchMembers}
                          >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Retry
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {members.map(member => (
                            <div
                              key={member.id}
                              className="form-check p-3 rounded"
                              style={{
                                backgroundColor: selectedMemberIds.includes(member.id) ? ARCA_GREEN + '15' : '#f8f9fa',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: selectedMemberIds.includes(member.id) ? `2px solid ${ARCA_GREEN}` : '1px solid #dee2e6'
                              }}
                              onClick={() => toggleMemberSelection(member.id)}
                            >
                              <input
                                className="form-check-input me-3"
                                type="checkbox"
                                checked={selectedMemberIds.includes(member.id)}
                                onChange={() => toggleMemberSelection(member.id)}
                                style={{ cursor: 'pointer' }}
                              />
                              <label className="form-check-label w-100" style={{ cursor: 'pointer' }}>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <div className="fw-semibold">
                                      {member.first_name} {member.last_name}
                                      {member.membership_type && (
                                        <span className="badge bg-secondary ms-2 text-xs">
                                          {member.membership_type}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-muted small">
                                      <i className="bi bi-envelope me-1"></i>
                                      {member.email}
                                    </div>
                                    {member.phone && (
                                      <div className="text-muted small">
                                        <i className="bi bi-telephone me-1"></i>
                                        {member.phone}
                                      </div>
                                    )}
                                  </div>
                                  {selectedMemberIds.includes(member.id) && (
                                    <i className="bi bi-check-circle-fill" style={{ color: ARCA_GREEN }}></i>
                                  )}
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <small className="form-text text-muted mt-2 d-block">
                      <i className="bi bi-info-circle me-1"></i>
                      Select one or multiple members to send bulk messages
                    </small>
                  </div>

                  {/* Message Content */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-pencil-square me-2" style={{ color: ARCA_GREEN }}></i>
                      Your Message
                    </label>
                    <textarea
                      className="form-control"
                      rows="8"
                      placeholder="Type your message here..."
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      style={{ borderColor: ARCA_GREEN, resize: 'vertical' }}
                    ></textarea>
                    <small className="form-text text-muted">
                      This will be sent as a private message within the ARCA system
                    </small>
                  </div>

                  {/* Info Box */}
                  {selectedMemberIds.length > 0 && (
                    <div className="alert border-0 d-flex align-items-start" style={{ backgroundColor: ARCA_GREEN + '15', borderLeft: `4px solid ${ARCA_GREEN}` }}>
                      <i className="bi bi-info-circle me-2 mt-1" style={{ color: ARCA_GREEN }}></i>
                      <div>
                        <strong>Ready to send:</strong> Your message will be sent to <strong>{selectedMemberIds.length}</strong> member{selectedMemberIds.length > 1 ? 's' : ''}.
                        {selectedMemberIds.length > 1 && ' Each member will receive an individual message.'}
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    onClick={() => setShowMessageModal(false)}
                    className="btn btn-secondary"
                    disabled={sendingMessage}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel
                  </button>
                  <button
                    onClick={sendDirectMessage}
                    className="btn btn-success"
                    disabled={sendingMessage || selectedMemberIds.length === 0 || !messageContent.trim()}
                    style={{ backgroundColor: ARCA_GREEN, borderColor: ARCA_GREEN }}
                  >
                    {sendingMessage ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending to {selectedMemberIds.length} member{selectedMemberIds.length > 1 ? 's' : ''}...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        Send to {selectedMemberIds.length} Member{selectedMemberIds.length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
};

export default ContactSubmissions;
